import { useEffect } from "react";
import {
  faCircleCheck,
  faInfoCircle,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./AttendanceScanDialog.css";

const DEFAULT_DURATION_MS = 3000;

/**
 * Attendance-only scan confirmation dialog.
 * Isolated from CustomAlerts so normal app toasts/confirms stay unchanged.
 */
export default function AttendanceScanDialog({
  open,
  message,
  status = "success",
  durationMs = DEFAULT_DURATION_MS,
  onClose,
}) {
  useEffect(() => {
    if (!open) return undefined;

    const timer = setTimeout(() => {
      onClose?.();
    }, durationMs);

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, message, status, durationMs, onClose]);

  if (!open || !message) return null;

  const statusClass =
    status === "error"
      ? "is-error"
      : status === "info"
        ? "is-info"
        : "is-success";

  const icon =
    status === "error"
      ? faTriangleExclamation
      : status === "info"
        ? faInfoCircle
        : faCircleCheck;

  return (
    <div
      className="att-scan-dialog-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        className={`att-scan-dialog ${statusClass}`}
        role="dialog"
        aria-modal="true"
        aria-live="assertive"
        aria-labelledby="att-scan-dialog-message"
      >
        <div className="att-scan-dialog__icon" aria-hidden="true">
          <FontAwesomeIcon icon={icon} />
        </div>
        <p
          id="att-scan-dialog-message"
          className={`att-scan-dialog__message${
            status === "success" ? " att-scan-dialog__message--name" : ""
          }`}
        >
          {message}
        </p>
        <div className="att-scan-dialog__progress" aria-hidden="true">
          <div
            className="att-scan-dialog__progress-bar"
            style={{ animationDuration: `${durationMs}ms` }}
          />
        </div>
      </div>
    </div>
  );
}
